import { useCallback, useEffect, useRef, useState } from 'react'
import { useSession } from '@hooks/useSession'
import {
  DirectPurchaseMethod,
  ICreateDirectPurchase,
  IDirectPurchaseContext,
  createDirectPurchase,
  getDirectPurchase,
  getDirectPurchaseStatus,
} from '@services/api/sparkecommerce/directPurchase'

export type DirectPurchaseStep =
  | 'loading'
  | 'error'
  | 'checkout'
  | 'ready'
  | 'processing'
  | 'pix'
  | 'pix_expired'
  | 'pending'
  | 'failed'
  | 'completed'

export const POLL_INTERVAL_MS = 2000
// Depois de meia hora esperando o Pix com o painel aberto, consulta mais devagar até ele vencer.
export const PIX_SLOW_POLL_INTERVAL_MS = 15 * 1000
// Aba escondida: só o suficiente para notar o pagamento quando o aluno voltar.
export const HIDDEN_POLL_INTERVAL_MS = 30 * 1000
const PIX_FAST_POLL_MS = 30 * 60 * 1000
// Até a primeira resposta: o cartão costuma responder em segundos; o QR Code do Pix pode demorar
// mais com a fila de compras lenta (ele também vai por e-mail).
export const ANSWER_LIMIT_MS: Record<DirectPurchaseMethod, number> = {
  credit_card: 2 * 60 * 1000,
  pix: 10 * 60 * 1000,
}
// O Pix do painel vale o mesmo que o do checkout (24 h, `pix_expiration_at`). Sem a data do
// servidor, usa esse prazo.
const PIX_DEFAULT_TTL_MS = 24 * 60 * 60 * 1000
// Pagamento feito no último minuto pode confirmar depois do vencimento: segue consultando um pouco.
// Também absorve relógio do aparelho um pouco fora (o vencimento é comparado com ele).
export const PIX_EXPIRY_GRACE_MS = 2 * 60 * 1000

const parseDate = (value?: string) => {
  if (!value) return undefined
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date
}

const isTabHidden = () =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden'

export function useDirectPurchase(offerId: string | null, open: boolean) {
  const { token } = useSession()
  const [context, setContext] = useState<IDirectPurchaseContext>()
  const [step, setStep] = useState<DirectPurchaseStep>('loading')
  const [pixCode, setPixCode] = useState<string>()
  const [pixExpiresAt, setPixExpiresAt] = useState<Date>()
  const [purchaseMethod, setPurchaseMethod] = useState<DirectPurchaseMethod>()
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  // Sessão acompanhada agora. Fechar o painel zera: a resposta que ainda estava a caminho
  // não reagenda nem mexe no estado.
  const polling = useRef<string | null>(null)
  const expiresAt = useRef<Date | undefined>(undefined)
  // Muda a cada abertura e fechamento do painel: a compra enviada antes de fechar não assume o
  // painel reaberto (nem para outro produto).
  const generation = useRef(0)
  // Token num ref: renovar a sessão não pode zerar um Pix aberto nem reabrir o painel.
  const tokenRef = useRef(token)
  tokenRef.current = token
  const hasToken = !!token
  // Cada envio cria uma compra nova: o segundo toque de um clique duplo não pode sair.
  const submitting = useRef(false)

  // Abrir e fechar o painel invalidam o que ainda estiver em andamento.
  const nextGeneration = () => {
    generation.current += 1
    return generation.current
  }

  const resetPix = () => {
    expiresAt.current = undefined
    setPixCode(undefined)
    setPixExpiresAt(undefined)
  }

  useEffect(() => {
    if (!open || !offerId || !hasToken) return

    const current = nextGeneration()
    setStep('loading')
    setPurchaseMethod(undefined)
    resetPix()
    getDirectPurchase(offerId, tokenRef.current)
      .then(({ data }) => {
        if (generation.current !== current) return
        setContext(data)
        setStep(data.checkout_reason ? 'checkout' : 'ready')
      })
      .catch(() => generation.current === current && setStep('error'))

    return () => {
      nextGeneration()
      polling.current = null
      clearTimeout(timer.current)
      // Sem isto o painel reaberto para outro produto renderiza o 'completed' da compra
      // anterior e avisa o pai de uma compra que não aconteceu.
      setStep('loading')
    }
  }, [open, offerId, hasToken])

  const poll = useCallback(
    (
      sessionId: string,
      attemptId: string,
      method: DirectPurchaseMethod,
      startedAt: number,
    ) => {
      const slow =
        method === 'pix' &&
        !!expiresAt.current &&
        Date.now() - startedAt > PIX_FAST_POLL_MS
      const interval = isTabHidden()
        ? HIDDEN_POLL_INTERVAL_MS
        : slow
          ? PIX_SLOW_POLL_INTERVAL_MS
          : POLL_INTERVAL_MS

      timer.current = setTimeout(async () => {
        try {
          const { data, status } = await getDirectPurchaseStatus(
            sessionId,
            attemptId,
            tokenRef.current,
          )
          if (polling.current !== sessionId) return
          // Sessão do aluno recusada: não dá para saber do pagamento por aqui. O e-mail avisa.
          if (status === 401 || status === 403) {
            polling.current = null
            return setStep('pending')
          }
          if (status !== 404 && data.status === 'completed') {
            polling.current = null
            return setStep('completed')
          }
          if (status !== 404 && data.status === 'failed') {
            polling.current = null
            return setStep('failed')
          }
          if (data?.pix?.pix_qr_code) {
            if (!expiresAt.current) {
              expiresAt.current =
                parseDate(data.pix.expiration_at) ??
                new Date(startedAt + PIX_DEFAULT_TTL_MS)
              setPixExpiresAt(expiresAt.current)
            }
            setPixCode(data.pix.pix_qr_code)
            setStep((current) => (current === 'pix_expired' ? current : 'pix'))
          }
        } catch {
          // Falha de rede no meio da espera: tenta de novo na próxima volta.
        }
        if (polling.current !== sessionId) return

        const now = Date.now()
        // Sem resposta do cartão, ou sem QR Code do Pix, no prazo: "em análise".
        // ponytail: desafio 3DS do cartão salvo também cai aqui; o checkout trata o desafio,
        // aqui ele não deve acontecer (cobrança de cartão guardado).
        if (!expiresAt.current && now - startedAt > ANSWER_LIMIT_MS[method]) {
          polling.current = null
          return setStep('pending')
        }
        if (expiresAt.current) {
          const expiry = expiresAt.current.getTime()
          if (now > expiry) setStep('pix_expired')
          if (now > expiry + PIX_EXPIRY_GRACE_MS) {
            polling.current = null
            return
          }
        }
        poll(sessionId, attemptId, method, startedAt)
      }, interval)
    },
    [],
  )

  const purchase = useCallback(
    async (body: ICreateDirectPurchase) => {
      if (!offerId || submitting.current) return
      submitting.current = true
      const current = generation.current
      polling.current = null
      clearTimeout(timer.current)
      resetPix()
      setPurchaseMethod(body.payment_method)
      setStep('processing')
      try {
        const { data } = await createDirectPurchase(
          offerId,
          body,
          tokenRef.current,
        )
        // Painel fechado (ou reaberto) enquanto a compra ia: ela segue no servidor e o aluno
        // recebe o e-mail, mas não mexe no painel atual.
        if (generation.current !== current) return
        polling.current = data.session_id
        poll(data.session_id, data.attempt_id, body.payment_method, Date.now())
      } catch {
        if (generation.current === current) setStep('failed')
      } finally {
        submitting.current = false
      }
    },
    [offerId, poll],
  )

  const retry = useCallback(() => {
    polling.current = null
    clearTimeout(timer.current)
    resetPix()
    setStep('ready')
  }, [])

  return {
    context,
    step,
    pixCode,
    pixExpiresAt,
    purchaseMethod,
    purchase,
    retry,
  }
}
